import type { ExcelChatMessage, ExcelTabsState, ExcelTab } from "../types";
import {
  FINSTER_QUERY_DATA_SOURCES,
  type FinsterQueryDataSource,
} from "../../shared/api/finster/constants";

/**
 * Per-(account, workbook) chat history persistence for the Excel taskpane.
 *
 * Keyed by `(accountScopeKey, workbookKey)` so two accounts on the same
 * machine can't see each other's chats and the same account gets a fresh
 * chat surface per workbook. The account scope is tenant-aware where a bare
 * user id is not: the same person switching company is a different account,
 * and their previous company's transcript — including its resume
 * affordances — must not follow them into the new one.
 * Backed by `sessionStorage` so history survives
 * taskpane reloads and Excel re-launches the embedded webview, but is
 * cleared when the FactSet Workstation / browser session ends — this
 * matches the "scoped per session" decision from the design Q&A and
 * keeps us off cross-device sync until/unless we add a backend.
 *
 * The workbook key is the FactSet host's `OpenWorkbookInfo.fullName`
 * (full path including filename) when available, or `.name` for unsaved
 * workbooks. Renaming / Save-As intentionally creates a new chat slot —
 * the old chat history stays under its old key for the rest of the
 * session in case the user opens the original file again.
 *
 * Transient tab fields (`isLoading`, `loadingStatus`, `pendingOperation`)
 * are stripped before save so a rehydrated state never starts in a half-loading
 * state and orphan pending-operation markers can't fire phantom replies on
 * reload against a Skill binding that no longer exists.
 * Persistent fields are preserved, with stored research-source identifiers
 * normalized during hydration for backward compatibility.
 */

const STORAGE_PREFIX = "finster_excel_chat_tabs";
const ANONYMOUS_ACCOUNT = "anonymous";
const ANONYMOUS_WORKBOOK = "__no_workbook__";
/**
 * Long enough that an account scope is never truncated: it carries a user id
 * or an email (254 characters at the RFC limit) plus a company identifier and
 * JSON punctuation. Truncation here would drop the company half of the key and
 * merge two tenants into one chat surface, which is the collision this key
 * exists to prevent.
 */
const MAX_ACCOUNT_KEY_LENGTH = 512;
const KNOWN_DATA_SOURCES = new Set<string>(FINSTER_QUERY_DATA_SOURCES);

export interface ExcelChatScope {
  /**
   * Tenant-aware account identity from `buildAccountScopeKey`. `null` before
   * login resolves.
   */
  accountScopeKey: string | null;
  /**
   * Workbook identifier. Prefer `OpenWorkbookInfo.fullName` (full path)
   * when the bridge exposes it; fall back to `.name` for new
   * unsaved workbooks. `null` before the bridge resolves or in
   * Office.js mode where workbook identity isn't available.
   */
  workbookKey: string | null;
}

function normalizeKey(value: string | null | undefined, fallback: string, max = 256): string {
  const trimmed = String(value ?? "").trim().toLowerCase();
  if (!trimmed) return fallback;
  return trimmed.replace(/[^a-z0-9@._\-]/g, "_").slice(0, max);
}

function getStorageKey(scope: ExcelChatScope): string {
  const account = normalizeKey(scope.accountScopeKey, ANONYMOUS_ACCOUNT, MAX_ACCOUNT_KEY_LENGTH);
  const workbook = normalizeKey(scope.workbookKey, ANONYMOUS_WORKBOOK);
  return `${STORAGE_PREFIX}:${account}:${workbook}`;
}

/**
 * Fields that may carry user-Skill guidance or other prompt-scoped model
 * input. Stripped from persisted messages by name so a future field added to
 * a run context is dropped by default rather than persisted by default.
 */
const MESSAGE_FIELDS_NEVER_PERSISTED = [
  "guidance",
  "selectedSkillsGuidance",
  "userSkillGuidance",
  "skillRunContext",
] as const;

function sanitizeMessageForStorage(message: ExcelChatMessage): ExcelChatMessage {
  const sanitized = { ...message } as ExcelChatMessage & Record<string, unknown>;
  let changed = false;
  for (const field of MESSAGE_FIELDS_NEVER_PERSISTED) {
    if (field in sanitized) {
      delete sanitized[field];
      changed = true;
    }
  }
  return changed ? sanitized : message;
}

function sanitizeTabForStorage(tab: ExcelTab): ExcelTab {
  const messages = tab.session?.messages ?? [];
  const sanitizedMessages = messages.map(sanitizeMessageForStorage);
  const messagesChanged = sanitizedMessages.some(
    (message, index) => message !== messages[index],
  );
  return {
    ...tab,
    isLoading: false,
    loadingStatus: "",
    agentTimeline: undefined,
    retrievedSources: undefined,
    retrievedCitations: undefined,
    retrievedCitedValuesByUuid: undefined,
    pendingOperation: undefined,
    ...(messagesChanged
      ? { session: { ...tab.session, messages: sanitizedMessages } }
      : {}),
  };
}

function sanitizeStateForStorage(state: ExcelTabsState): ExcelTabsState {
  return {
    ...state,
    tabs: state.tabs.map(sanitizeTabForStorage),
  };
}

function hydrateStoredTab(tab: ExcelTab): ExcelTab {
  const storedIds = Array.isArray(tab.session?.selectedUserFileIds)
    ? tab.session.selectedUserFileIds
    : [];
  const selectedUserFileIds = Array.from(
    new Set(
      storedIds
        .filter((fileId): fileId is string => typeof fileId === "string")
        .map((fileId) => fileId.trim())
        .filter(Boolean)
    ),
  );
  const storedDataSources = tab.session?.selectedDataSources;
  const selectedDataSources = Array.isArray(storedDataSources)
    ? Array.from(
        new Set(
          storedDataSources.filter(
            (source): source is FinsterQueryDataSource =>
              typeof source === "string" &&
              KNOWN_DATA_SOURCES.has(source),
          ),
        ),
      )
    : undefined;

  return {
    ...tab,
    session: {
      ...tab.session,
      selectedDataSources,
      selectedUserFileIds,
    },
  };
}

/**
 * Load persisted chat tabs for the given scope. Returns `null` when
 * the scope is incomplete (no account / no workbook), no entry exists,
 * or the stored payload fails the minimal `tabs: ExcelTab[]` shape
 * check — caller falls back to the default empty state in any of
 * those cases.
 */
export function loadExcelChatTabs(scope: ExcelChatScope): ExcelTabsState | null {
  if (typeof sessionStorage === "undefined") return null;
  if (!scope.accountScopeKey || !scope.workbookKey) return null;
  try {
    const raw = sessionStorage.getItem(getStorageKey(scope));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ExcelTabsState>;
    if (!parsed || !Array.isArray(parsed.tabs) || parsed.tabs.length === 0) {
      return null;
    }
    if (typeof parsed.activeTabId !== "string") return null;
    const storedState = parsed as ExcelTabsState;
    return {
      ...storedState,
      tabs: storedState.tabs.map(hydrateStoredTab),
    };
  } catch {
    return null;
  }
}

/**
 * Persist the given tabs state under the scope. Returns whether the browser
 * storage write succeeded. Storage exceptions (quota, disabled storage) stay
 * isolated from the in-memory chat experience, while migration callers can
 * retain the previous recovery slot when a write fails. Skips writes when the
 * scope is incomplete to avoid creating an `anonymous` blob that would never
 * be read.
 */
export function saveExcelChatTabs(scope: ExcelChatScope, state: ExcelTabsState): boolean {
  if (typeof sessionStorage === "undefined") return false;
  if (!scope.accountScopeKey || !scope.workbookKey) return false;
  try {
    sessionStorage.setItem(
      getStorageKey(scope),
      JSON.stringify(sanitizeStateForStorage(state))
    );
    return true;
  } catch {
    // QuotaExceeded or disabled storage — drop silently. Could
    // implement LRU eviction across `STORAGE_PREFIX:*` keys later
    // if this becomes a real problem in production.
    return false;
  }
}

/**
 * Remove the persisted tabs for the given scope. Used by sign-out
 * flows or "clear chat history" UI affordances.
 */
export function clearExcelChatTabs(scope: ExcelChatScope): void {
  if (typeof sessionStorage === "undefined") return;
  if (!scope.accountScopeKey || !scope.workbookKey) return;
  try {
    sessionStorage.removeItem(getStorageKey(scope));
  } catch {
    // ignore
  }
}
